
import { ReducerRegistry } from '../base/redux';

import {
    ADD_MESSAGE,
    CLEAR_MESSAGES,
    CLOSE_CHAT,
    CLOSE_IMAGE_PREVIEW,
    CLOSE_POLLS,
    DELETE_MESSAGE,
    EDIT_MESSAGE,
    OPEN_CHAT,
    OPEN_IMAGE_PREVIEW,
    OPEN_POLLS,
    RESTORE_MESSAGE,
    SET_IS_POLL_TAB_FOCUSED,
    SET_PRIVATE_CHAT_DISABLED,
    SET_PRIVATE_MESSAGE_RECIPIENT
} from './actionTypes';
import { MESSAGE_RESTORE_TIMEOUT } from './constants';

const DEFAULT_STATE = {
    isOpen: false,
    isPollsTabFocused: false,
    isPollsOpen: false,
    lastReadMessage: undefined,
    lastReadPoll: undefined,
    messages: [],
    nbUnreadMessages: 0,
    privateMessageRecipient: undefined,
    imagePreview: {
        isOpen: false,
        imageUrl: null,
        fileName: null
    },
    isPrivateChatDisabled: false,
    deletedMessageIds: []
};

ReducerRegistry.register('features/chat', (state = DEFAULT_STATE, action) => {
    switch (action.type) {
    case ADD_MESSAGE: {
        const isDeleted = Boolean(action.deleteChat)
            || Boolean(action.messageId && state.deletedMessageIds?.includes(action.messageId));

        if (state.messages.some(m => (action.messageId && m.messageId === action.messageId)
            || (!action.messageId && m.message === action.message && m.id === action.id))) {
            return state;
        }

        const newMessage = {
            displayName: action.displayName,
            error: action.error,
            id: action.id,
            isReaction: action.isReaction,
            messageId: action.messageId,
            messageType: action.messageType,
            message: action.message,
            privateMessage: action.privateMessage,
            recipient: action.recipient,
            recipientId: action.recipientId,
            timestamp: action.timestamp,
            deleteChat: isDeleted,
            deletedAt: action.deletedAt || (isDeleted ? Date.now() : undefined)
        };

        // React native, unlike web, needs a reverse sorted message list.
        const messages
        = navigator.product === 'ReactNative'
            ? [ newMessage, ...state.messages ]
            : [ ...state.messages, newMessage ];

        return {
            ...state,
            lastReadMessage: action.hasRead ? newMessage : state.lastReadMessage,
            nbUnreadMessages: state.isPollsTabFocused
                ? state.nbUnreadMessages + 1
                : state.nbUnreadMessages,
            messages
        };
    }

    case CLEAR_MESSAGES:
        return {
            ...state,
            lastReadMessage: undefined,
            messages: []
        };

    case EDIT_MESSAGE: {
        let found = false;
        const newMessage = action.message;
        const messages = state.messages.map(m => {
            if (m.messageId === newMessage.messageId) {
                found = true;
                const textChanged = newMessage.message !== undefined && newMessage.message !== m.message;
                const isEdited = newMessage.isEdited === undefined
                    ? textChanged || Boolean(m.isEdited)
                    : Boolean(newMessage.isEdited);
                const updated = {
                    ...m,
                    ...newMessage,
                    isEdited
                };

                if (updated.newMessageId) {
                    updated.messageId = updated.newMessageId;
                    delete updated.newMessageId;
                }

                return updated;
            }

            return m;
        });

        // no change
        if (!found) {
            return state;
        }

        return {
            ...state,
            messages
        };
    }

    case SET_PRIVATE_MESSAGE_RECIPIENT:
        return {
            ...state,
            privateMessageRecipient: action.participant
        };

    case OPEN_POLLS:
        return {
            ...state,
            isPollsOpen: true
        };
    case CLOSE_POLLS:
        return {
            ...state,
            isPollsOpen: false
        };

    case OPEN_CHAT:
        return {
            ...state,
            isOpen: true,
            privateMessageRecipient: action.participant,
            nbUnreadMessages: state.isPollsTabFocused ? state.nbUnreadMessages : 0
        };

    case DELETE_MESSAGE: {
        const messageId = action.messageId;
        const deletedAt = action.deletedAt || Date.now();
        const messages = state.messages.map(m => {
            if (m.messageId === messageId) {
                return {
                    ...m,
                    deleteChat: true,
                    deletedAt: m.deletedAt || deletedAt
                };
            }

            return m;
        });

        const deletedMessageIds = state.deletedMessageIds || [];
        const nextDeletedMessageIds = messageId && !deletedMessageIds.includes(messageId)
            ? [ ...deletedMessageIds, messageId ]
            : deletedMessageIds;

        return {
            ...state,
            messages,
            deletedMessageIds: nextDeletedMessageIds
        };
    }

    case RESTORE_MESSAGE: {
        const messageId = action.messageId;
        const targetMessage = state.messages.find(m => m.messageId === messageId);
        const targetText = targetMessage?.message || '';
        const isFile = Boolean(targetText && (targetText.includes('class="file-attachment"') || targetText.includes('<a ')));

        if (isFile) {
            return state;
        }

        if (targetMessage?.deletedAt && (Date.now() - targetMessage.deletedAt > MESSAGE_RESTORE_TIMEOUT)) {
            return state;
        }

        const messages = state.messages.map(m => {
            if (m.messageId === messageId) {
                return {
                    ...m,
                    deleteChat: false,
                    deletedAt: undefined
                };
            }

            return m;
        });

        const deletedMessageIds = (state.deletedMessageIds || []).filter(
            id => id !== messageId
        );

        return {
            ...state,
            messages,
            deletedMessageIds
        };
    }

    case CLOSE_CHAT:
        return {
            ...state,
            isOpen: false,
            lastReadMessage:
          state.messages[
            navigator.product === 'ReactNative' ? 0 : state.messages.length - 1
          ],
            privateMessageRecipient: action.participant
        };

    case SET_IS_POLL_TAB_FOCUSED: {
        return {
            ...state,
            isPollsTabFocused: action.isPollsTabFocused
        };
    }
    case SET_PRIVATE_CHAT_DISABLED:
        return {
            ...state,
            isPrivateChatDisabled: action.disabled,
            privateMessageRecipient: action.disabled ? undefined : state.privateMessageRecipient
        };

    case OPEN_IMAGE_PREVIEW: {
        return {
            ...state,
            imagePreview: {
                isOpen: true,
                imageUrl: action.imageUrl,
                fileName: action.fileName || null
            }
        };
    }

    case CLOSE_IMAGE_PREVIEW: {
        return {
            ...state,
            imagePreview: {
                isOpen: false,
                imageUrl: null,
                fileName: null
            }
        };
    }
    }

    return state;
});
